import { defineStore } from "pinia";

export const useNonPersistentStore = defineStore("nonPersistent", {
  state: () => ({
    // customer email
    customerEmail: null,
    // customer phone
    customerPhone: null,
    // customer name
    customerName: null,
    // unique id for the user associated with this browser,
    uniqueId: null,
    // currently logged in firebase user
    currentFirebaseUser: null,
    // currently logged in band
    currentlyLoggedInBand: undefined,
    // current user from database
    currentUser: undefined,
    // returns if the current user is an admin
    isAdmin: false,
    // the selected band that is being backdoored into
    otherBand: undefined,
    // controls whether or not ads are being shown in the current session
    displayAds: true,
    // controls whether or not the user has seen up to a certain point in the onboarding
    disableProfileTextNotificationsStep: false,
    // image upload progress
    imageUploadProgress: 0,
    // the local song passes that are stored
    localSongPasses: [],
    // current gig stats
    userVotingForGigs: {},
    // kidzbop over 18
    hasBeenAskedForAge: false,
    isOver18: false,
    optIntoEmails: false,
    hasOptedIntoEmails: false,
    // URL to redirect to after login
    redirectAfterLogin: null,
    // states that are tied to subscriptions
    // these are the snapshot states that get updated
    currentBand: undefined,
    currentBrandingConfig: undefined,
    currentGig: undefined,
    currentSetlist: undefined,
    currentSetlistWithGigMetadata: undefined,
    currentTips: undefined,
    currentTipsGroupedForGig: undefined,
    currentEvent: undefined,
    currentGigEvents: undefined,
    currentEventTransactions: undefined,
    currentSongResets: undefined,
    currentShoutoutsAuth: undefined,
    currentShoutoutsSafe: undefined,
    currentGigExperiences: undefined,
    currentGigExperiencesTransactions: undefined,
    currentGigPhraseBoards: undefined,
    currentGigPhraseBoardTransactions: undefined,
    currentMerchandiseForBand: undefined,
    currentMerchandiseTransactions: undefined,
    currentMerchandise: undefined,
    currentSongBattleTransactions: undefined,
    currentLivecastState: undefined,
    currentLiveFormTransactions: undefined,
    eventCompanion: undefined,
    eventCompanionTransactions: undefined,
    // these are the actual subscriptions
    bandSubscription: undefined,
    brandingConfigSubscription: undefined,
    gigSubscription: undefined,
    setlistSubscription: undefined,
    setlistSubscriptionWithGigMetadata: undefined,
    tipsSubscription: undefined,
    songResetsSubscription: undefined,
    merchandiseSubscription: undefined,
    eventCompanionSubscription: undefined,
    // new types of subscriptions
    tipsGroupedForGigSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentShoutoutsAuthSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentShoutoutsSafeSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentGigExperiencesSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentGigExperiencesTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentGigPhraseBoardsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentGigPhraseBoardTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentMerchandiseSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentMerchandiseTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentSongBattleTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentLivecastStateSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    eventSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    eventTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    gigEventsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    currentLiveFormTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    eventCompanionTransactionsSubscription: {
      lastUpdateTime: undefined,
      subscription: undefined,
      interval: undefined,
    },
    // currency options,
    selectedCurrency: {
      value: "USD",
      text: "USD",
      name: "United States Dollar",
      symbol: "$",
    },
    currencyOptions: [
      { value: "USD", text: "USD", name: "United States Dollar", symbol: "$" },
      { value: "CAD", text: "CAD", name: "Canadian Dollar", symbol: "C$" },
    ],
    needToDetermineStep: true,
    isProgrammaticNavigation: false,
  }),

  getters: {
    // Lazy getter for persistent store to avoid circular dependency
    _persistentStore() {
      const { usePersistentStore } = require("./persistent");
      return usePersistentStore();
    },
    isLoggedIn: (state) => !!state.currentFirebaseUser,
    songs: (state) => state.songs,
    setlists: (state) => state.setlists,
    bandPageName: (state) => state.bandPageName,
    currencyType: (state) => state.selectedCurrency.value,
  },

  actions: {
    setProgrammaticNavigation(value) {
      this.isProgrammaticNavigation = value;
    },

    setIsOver18(value) {
      this.isOver18 = value;
      this.hasBeenAskedForAge = true;
      this._persistentStore.setIsOver18(value);
    },

    setOptIntoEmails(value) {
      this.optIntoEmails = value;
      this._persistentStore.setOptIntoEmails(value);
    },

    setHasOptedIntoEmails(value) {
      this.hasOptedIntoEmails = value;
      this._persistentStore.setHasOptedIntoEmails(value);
    },

    setLocalSongPasses(value) {
      this.localSongPasses = value;
      this._persistentStore.setLocalSongPasses(value);
    },

    setImageUploadProgress(value) {
      this.imageUploadProgress = value;
    },

    setNeedToDetermineStep(value) {
      this.needToDetermineStep = value;
    },

    setCustomerEmail(value) {
      this.customerEmail = value;
      this._persistentStore.setCustomerEmail(value);
    },

    setCustomerPhone(value) {
      this.customerPhone = value;
      this._persistentStore.setCustomerPhone(value);
    },

    setUniqueId(id) {
      this.uniqueId = id;
      this._persistentStore.setUniqueId(id);
    },

    setCustomerName(value) {
      this.customerName = value;
      this._persistentStore.setCustomerName(value);
    },

    // selected band for backdoor
    setOtherBand(other) {
      this.otherBand = other;
    },

    // other
    setCurrentFirebaseUser(currentFirebaseUser) {
      this.currentFirebaseUser = currentFirebaseUser;
      if (!currentFirebaseUser || currentFirebaseUser === null) {
        this.currentlyLoggedInBand = undefined;
        try {
          this._persistentStore.setCurrentlyLoggedInBand(null);
        } catch (error) {
          console.warn("Failed to set currentlyLoggedInBand in persistent store:", error);
        }
      } else if (!this.currentlyLoggedInBand) {
        try {
          this.currentlyLoggedInBand = this._persistentStore.currentlyLoggedInBandParsed;
        } catch (error) {
          console.warn("Failed to get currentlyLoggedInBandParsed from persistent store:", error);
        }
      }
    },

    setCurrentlyLoggedInBand(band) {
      this.currentlyLoggedInBand = band;
      this._persistentStore.setCurrentlyLoggedInBand(band);
    },

    setDisableProfileTextNotificationsStep() {
      this.disableProfileTextNotificationsStep = true;
      this._persistentStore.setDisableProfileTextNotificationsStep(true);
    },

    setSelectedCurrency(currency) {
      this.selectedCurrency = currency;
      this._persistentStore.setSelectedCurrency(currency);
    },

    setUserVotingForGigs(gigStats) {
      this.userVotingForGigs = gigStats;
    },

    reportTipAtGig(gigId) {
      if (!this.userVotingForGigs[gigId]) {
        this.userVotingForGigs[gigId] = {
          numVotes: 1,
        };
      } else {
        this.userVotingForGigs[gigId].numVotes += 1;
      }
      this._persistentStore.reportTipAtGig(gigId);
    },

    setRedirectAfterLogin(url) {
      this.redirectAfterLogin = url;
    },
  },
});
